import React, { useEffect, useMemo, useState } from 'react';
import Table from '.';
import Loader from '../../atoms/Loader';
import { LoaderMode } from '../../atoms/Loader/enums';
import Placeholder from '../Placeholder';
import { useTableData } from './hooks';
import { DataTableProps, PageSizeOption } from './types';

const DEFAULT_PAGE_SIZE_OPTIONS: PageSizeOption[] = [
  { label: '10', value: 10 },
  { label: '25', value: 25 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
];

// eslint-disable-next-line @typescript-eslint/ban-types
function DataTable<T extends object>({
  endpoint,
  placeholderProps,
  tableAPIRef,
  filterItems,
  queryOptions,
  query = {},
  pageSize: initialPageSize = 10,
  mockData,
  pageParamKey = 'page',
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  showPageSizeSelect = true,
  ...props
}: DataTableProps<T>): ReactComponentReturnType {
  const [page, setPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(initialPageSize);

  const { loading, data, refetch, pageCount, totalCount } = useTableData<T>(
    endpoint,
    page,
    currentPageSize,
    query,
    pageParamKey,
    queryOptions
  );

  const handlePageSizeChange = (value: string) => {
    const newSize = parseInt(value, 10);
    setCurrentPageSize(newSize);
    setPage(1);
  };

  const selectOptions = useMemo(
    () =>
      pageSizeOptions.map((opt) => ({
        label: opt.label,
        value: String(opt.value),
      })),
    [pageSizeOptions]
  );

  const tableData = mockData ?? data;

  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(query)]);

  useEffect(() => {
    if (!tableAPIRef) {
      return;
    }

    tableAPIRef({
      page,
      pageSize: currentPageSize,
      loading,
      isEmpty: data.length === 0,
      totalCount,
      refetch,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, refetch, currentPageSize]);

  const filteredData = useMemo(() => {
    if (!filterItems) {
      return tableData;
    }

    return tableData.filter(filterItems);
  }, [tableData, filterItems]);

  return loading ? (
    <Loader mode={LoaderMode.Table} />
  ) : (
    <>
      {filteredData.length ? (
        <Table<T>
          data={filteredData}
          onPageChange={setPage}
          disabledPagination={totalCount === undefined}
          pageSize={
            totalCount === undefined ? tableData.length : currentPageSize
          }
          currentPage={page}
          showPageSizeSelect={showPageSizeSelect}
          pageSizeSelectOptions={selectOptions}
          onPageSizeChange={handlePageSizeChange}
          {...{ ...props, pageCount, totalCount }}
        />
      ) : (
        <Placeholder {...placeholderProps} />
      )}
    </>
  );
}

export default DataTable;
